<template>
  <div class="product-card" :class="productClass">
    <div class="main-card" :class="{ 'in-store-card': isInStoreAvailable }">
      <div class="product-thumbnail-wrapper-container">
        <ProductThumbnailWrapper
          :product="product"
          :image-badge="imageBadge"
          :image-width="302"
          :fire-event="true"
          :image-fetch-priority="imagePriority"
          v-bind="productProps"
          @toggle-product-slideout="handleToggleProductInfoSlideout"
        />
      </div>

      <div class="product-card-footer" :class="productFooterClass">
        <div v-if="isRegistrant || (!isRegistrant && remainQuantity > 0)" class="footer-description grid-x medium">
          <p class="margin-right-xs" data-fs-mask>
            {{ product?.purchased.sold }} of {{ product?.purchased.quantity }} {{ registrantProductCard.purchasedText }}
          </p>
          <p v-if="stockMessage" class="warning-message">
            {{ stockMessage }}
          </p>
        </div>

        <template v-if="isRegistrant">
          <div class="grid-x">
            <div class="cell">
              <div class="grid-x">
                <div class="cell large-4 edit-container">
                  <AtomicUiButton variant="secondary" :expanded="true" @click.stop="toggleEditSlideout('edit')">Edit</AtomicUiButton>
                </div>
                <div class="cell large-8 large-padding-left-xxs atb-container">
                  <AtomicUiButton
                    v-if="isAddToBagButtonVisible"
                    variant="primary"
                    :expanded="true"
                    :loading="isAddToBagLoading"
                    @click.stop="toggleAddToBagSlideout"
                  >
                    {{ registrantProductCard.addToBagButtonText }}
                  </AtomicUiButton>
                  <AtomicUiButton v-if="disabledButtonText" variant="secondary" :expanded="true" :disabled="true">
                    {{ disabledButtonText }}
                  </AtomicUiButton>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div v-if="isAddToBagButtonVisible" class="grid-x cta-container">
            <div class="cell small-12 medium-12 large-5 xlarge-4 select-container large-padding-right-xxs">
              <select :id="`quantity-select-${product.id}`" v-model="selectedQuantity" name="dropdown" class="selected">
                <option v-for="(quantity, index) in remainQuantity" :key="index" :value="quantity">
                  {{ quantity }}
                </option>
              </select>
              <label :for="`quantity-select-${product.id}`" class="select-label-top selected-label-top">Qty</label>
            </div>
            <div class="cell small-12 medium-12 large-7 xlarge-8 large-padding-right-m atb-container">
              <AtomicUiButton variant="primary" :expanded="true" name="addToBagCTA" :loading="isAddToBagLoading" @click="toggleAddToBagSlideout">
                {{ giftGiverProductCard.addToBagButtonText }}
              </AtomicUiButton>
            </div>
          </div>
          <div v-if="disabledButtonText" class="large-padding-right-l cta-container disabled-cta-container">
            <AtomicUiButton variant="secondary" :expanded="true" :disabled="true">
              {{ disabledButtonText }}
            </AtomicUiButton>
          </div>
        </template>
      </div>
      <div v-if="isRegistrant" :id="`moreLikeThisButton-${indexVal}`" class="more-like-this-button">
        <AtomicUiButton
          v-if="displayMoreLikeThis"
          variant="link"
          class="more-like-this margin-top-xxs margin-botton-l"
          @click="openMoreLikeThis(indexVal)"
        >
          {{ registrantProductCard.moreLikeThisCTA }}
          <span
            :id="`index-${indexVal + 1}`"
            :data-id="`index-${indexVal + 1}`"
            :class="isMoreLikeThisOpen ? 'chevron-medium-up-black chevron' : 'chevron-medium-down-black chevron'"
            class="chevron"
          ></span>
        </AtomicUiButton>

        <div v-if="isMoreLikeThisOpen">
          <ProsWrapper
            :popular-items="false"
            :product-id="product.id"
            :image-id="imageId"
            :target-left-margin="targetLeftMargin"
            :my-gift-width="myGiftWidth"
          />
          <!-- Also ask pros wgl team to update component to receive image id -->
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import ProductThumbnailWrapper from '~/common/ProductThumbnailWrapper';
import ProsWrapper from '~/components/ProsWrapper';
import { registrantProductCard, giftGiverProductCard } from 'config';
import { useEnvPropsStore } from '~/stores/envProps';

const props = defineProps({
  productItem: {
    type: Object,
    required: true,
  },
  indexVal: {
    type: Number,
    required: false,
  },
  storedIndex: {
    type: Number,
    required: false,
  },
  isRegistrant: {
    type: Boolean,
    required: false,
    default: false,
  },
  isAddToBagLoading: {
    type: Boolean,
    default: false,
  },
  imagePriority: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(['toggleAddToBagSlideout', 'toggleProductInfoSlideout', 'toggleEditSlideout', 'storeProductIndex']);
const envPropsStore = useEnvPropsStore();
const product = ref(props.productItem);
const selectedQuantity = ref(1);
const isAddToBagButtonVisible = ref(true);
const disabledButtonText = ref('');
const isMoreLikeThisOpen = ref(false);
const stockMessage = ref('');
const targetLeftMargin = ref(null);
const imageBadge = reactive({
  enableBadge: false,
  badgeText: '',
});
const myGiftWidth = reactive({
  leftVal: null,
  widthVal: null,
});
const isInStoreAvailable = ref(false);
let displayMoreLikeThis;
const productCardConfig = {
  registrantProductCard,
  giftGiverProductCard,
};

const isMcom = computed(() => envPropsStore.envProps?.entryPoint?.toUpperCase() === 'MCOM');
const remainQuantity = computed(() => {
  if (product.value?.purchased?.sold >= product.value?.purchased?.quantity) return 0;
  const remQty = product.value?.purchased?.quantity - product.value?.purchased?.sold;
  return remQty > product.value?.purchased?.maxQuantity ? product.value?.purchased?.maxQuantity : remQty;
});
const productCardType = computed(() => `${props.isRegistrant ? 'registrant' : 'giftGiver'}ProductCard`);
const productClass = computed(() => [
  props.isRegistrant ? 'registrant-product-card' : 'gift-giver-product-card',
  props.isRegistrant && displayMoreLikeThis ? 'moreLikeThis-product-card' : '',
  props.isRegistrant && isMoreLikeThisOpen.value ? 'moreLikeThisOpen-product-card' : '',
]);
const productFooterClass = computed(() => ({
  'disabled-button-footer': !props.isRegistrant && disabledButtonText.value,
  'no-quantity-footer': !props.isRegistrant && remainQuantity.value <= 0,
}));
const productProps = computed(() => {
  if (props.isRegistrant) {
    return {
      'enable-click-on-brand-and-product': false,
      'stock-message': stockMessage.value,
    };
  }
  return {};
});

const toggleProductInfoSlideout = () => {
  if (isInStoreAvailable.value) return;
  emit('toggleProductInfoSlideout', product.value);
};
const toggleAddToBagSlideout = () => {
  emit('toggleAddToBagSlideout', product.value, Number(selectedQuantity.value));
};
const toggleEditSlideout = (isImageOrEditClicked) => {
  if (isInStoreAvailable.value && isImageOrEditClicked === 'image click') return;
  emit('toggleEditSlideout', product.value);
  if (isImageOrEditClicked === 'image click') {
    if (isMcom.value) {
      fireTag('link', {
        event_name: 'edit registry image click',
        custom_link_name: 'edit product image click',
      });
    } else {
      fireTag('link', {
        event_name: 'edit product image click',
        custom_link_name: 'edit registry image click',
        link_name: 'edit registry image click',
      });
    }
  } else {
    fireTag('link', {
      event_name: `registry product details ${isImageOrEditClicked}`,
      product_brand: [product.value?.detail?.brand.name],
      product_id: [product.value?.id],
      product_name: [product.value?.detail?.name],
      product_price: [product.value?.price],
    });
  }
};
const openMoreLikeThis = (indexVal) => {
  isMoreLikeThisOpen.value = !isMoreLikeThisOpen.value;
  targetLeftMargin.value = document.getElementById(`moreLikeThisButton-${indexVal}`)?.getBoundingClientRect().left;
  myGiftWidth.leftVal = document.getElementsByClassName('myGiftTab')[0]?.getBoundingClientRect().left;
  myGiftWidth.widthVal = document.getElementsByClassName('myGiftTab')[0]?.getBoundingClientRect().width;
  emit('storeProductIndex', indexVal);
};

const handleToggleProductInfoSlideout = () => {
  if (props.isRegistrant) {
    toggleEditSlideout('image click');
  } else {
    toggleProductInfoSlideout();
  }
};

const isPurchased = computed(() => product.value?.purchased?.quantity <= product.value?.purchased?.sold);
const isMew = computed(() => envPropsStore.envProps.isMew);

const bigTicketItemInfoText = computed(() => productCardConfig[productCardType.value][isMew.value ? 'bigTicketItemLink' : 'bigTicketItem']);
const outOfStockOnlineAllChannelBadgeText = computed(
  () => productCardConfig[productCardType.value][isMew.value ? 'outOfStockOnlineAllChannelBadgeLink' : 'outOfStockOnlineAllChannelBadgeText'],
);
const inStoreBopsBossInfoText = computed(
  () => productCardConfig[productCardType.value][isMew.value ? 'inStoreBopsBossInfoLink' : 'inStoreBopsBossInfoText'],
);

const imageId = computed(() => {
  const imageFilePath = product.value.imagery.primaryImage.filePath;
  // example of imageFilePath = '8/optimized/20527808_fpx.tif'
  const urlSubstring = '/optimized/';
  const imageIdValue = imageFilePath.slice(imageFilePath.indexOf(urlSubstring) + urlSubstring.length, imageFilePath.indexOf('_'));
  return Number(imageIdValue);
});

const available = ref(product.value?.networkAvailability?.[0]?.available);
const lowAvailable = ref(product.value?.networkAvailability?.[0]?.lowAvailable);
const fulfillmentChannel = ref(product.value?.networkAvailability?.[0]?.fulfillmentChannel);
const fulfillmentMethod = ref(product.value?.networkAvailability?.[0]?.fulfillmentMethod);
const discontinuedCode = ref(product.value?.networkAvailability?.[0]?.discontinuedCode);
const bigTicketItemFlag = ref(product.value?.bigTicketItemFlag);

if (isPurchased.value) {
  // State: Purchased/Completed
  disabledButtonText.value = productCardConfig[productCardType.value].purchasedButtonText;
  isAddToBagButtonVisible.value = false;
  imageBadge.enableBadge = true;
  imageBadge.badgeText = productCardConfig[productCardType.value].purchasedBadgeText;
} else if (props.isRegistrant && !available.value && discontinuedCode.value?.toUpperCase() === 'Y') {
  // State: Discontinued out of stock
  disabledButtonText.value = registrantProductCard.discontinuedOutOfStockButtonText;
  isAddToBagButtonVisible.value = false;
  imageBadge.enableBadge = true;
  imageBadge.badgeText = registrantProductCard.discontinuedOutOfStockBadgeText;
  displayMoreLikeThis = true;
} else if (available.value && discontinuedCode.value?.toUpperCase() === 'Y') {
  // State: Discontinued in stock
  isAddToBagButtonVisible.value = true;
  if (props.isRegistrant) {
    imageBadge.enableBadge = true;
    imageBadge.badgeText = registrantProductCard.discontinuedInStockBadgeText;
    displayMoreLikeThis = true;
  } else {
    stockMessage.value = giftGiverProductCard.discontinuedInStockInfoText;
  }
} else if (bigTicketItemFlag.value) {
  // Big Ticket Item
  disabledButtonText.value = productCardConfig[productCardType.value].inStoreBopsBossButtonText;
  isAddToBagButtonVisible.value = false;
  product.value.storeOnly = {
    inStoreOnly: true,
    message: bigTicketItemInfoText.value,
  };
  isInStoreAvailable.value = true;
} else if (!available.value && (!fulfillmentMethod.value?.includes('STH') || fulfillmentMethod.value?.length <= 0)) {
  // OOS for online, OOS for all channel
  disabledButtonText.value = productCardConfig[productCardType.value].outOfStockOnlineAllChannelButtonText;
  isAddToBagButtonVisible.value = false;
  imageBadge.enableBadge = true;
  imageBadge.badgeText = outOfStockOnlineAllChannelBadgeText.value;
  if (props.isRegistrant) {
    displayMoreLikeThis = true;
  }
} else if (
  !fulfillmentMethod.value?.includes('STH') &&
  (fulfillmentMethod.value?.includes('STOREONLY') || fulfillmentMethod.value?.includes('BOPS') || fulfillmentMethod.value?.includes('BOSS'))
) {
  // State: In-Store Only, BOPS only UPC, BOSS only UPC
  disabledButtonText.value = productCardConfig[productCardType.value].inStoreBopsBossButtonText;
  isAddToBagButtonVisible.value = false;
  product.value.storeOnly = {
    inStoreOnly: true,
    message: inStoreBopsBossInfoText.value,
  };
  isInStoreAvailable.value = true;
} else if (available.value && fulfillmentChannel.value?.toUpperCase() === 'ORDR') {
  // State: Backordered
  isAddToBagButtonVisible.value = true;
  if (props.isRegistrant) {
    imageBadge.enableBadge = true;
    imageBadge.badgeText = registrantProductCard.backorderedBadgeText;
    displayMoreLikeThis = true;
  } else {
    stockMessage.value = giftGiverProductCard.backorderedInfoText;
  }
} else if (available.value && fulfillmentChannel.value?.toUpperCase() === 'SPEC') {
  // State: Special Order
  isAddToBagButtonVisible.value = true;
  if (props.isRegistrant) {
    imageBadge.enableBadge = true;
    imageBadge.badgeText = registrantProductCard.specialOrder;
  } else {
    stockMessage.value = giftGiverProductCard.specialOrder;
  }
} else if (available.value && fulfillmentMethod.value?.includes('STH')) {
  // State: Available
  isAddToBagButtonVisible.value = true;
  if (lowAvailable.value) {
    // State: Low in Stock
    stockMessage.value = productCardConfig[productCardType.value].lowInStockInfoText;
  }
} else if (available.value && lowAvailable.value) {
  // State: Low in Stock
  isAddToBagButtonVisible.value = true;
  stockMessage.value = productCardConfig[productCardType.value].lowInStockInfoText;
}

watch(
  () => props.storedIndex,
  () => {
    if (props.storedIndex !== props.indexVal) {
      isMoreLikeThisOpen.value = false;
    }
  },
);
</script>

<style lang="scss" scoped>
@import '~@core/csg/src/scss/encodedIcons/mcom/_chevron-medium-down-black.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_chevron-medium-up-black.scss';
@import '~@core/foundation/scss/foundation-6.7.4/scss/prototype/_spacing.scss';

.product-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .main-card {
    :deep(.product-thumbnail-height) {
      height: auto;
    }

    :deep(.product-thumbnail-image-container) {
      padding-bottom: initial;
      aspect-ratio: 0.7975;
    }

    :deep(.v-product-thumbnail-image-container) {
      padding-bottom: initial;
      aspect-ratio: 0.7975;
    }

    :deep(.in-store-call-link) {
      color: $sale-color;
    }

    :deep(.badge-icon) {
      @include margin-direction('top', map-get($spacer, 'xxs'));
    }
  }

  .warning-message {
    color: $error-color;
  }
}

.registrant-product-card {
  .product-card-footer {
    margin-top: auto;

    .footer-description {
      @include breakpoint(medium up) {
        margin-top: rem-calc(12);
        margin-bottom: rem-calc(12);
        height: auto;
      }

      @include breakpoint(small down) {
        align-items: end;
        margin-top: 0;
        @include margin-direction('bottom', map-get($spacer, 'xxs'));
      }
    }
  }

  .moreLikeThisOpen-product-card {
    height: 100%;
  }

  .more-like-this {
    align-self: flex-start;
    font-weight: 500;
    line-height: rem-calc(20);
    cursor: pointer;
  }

  .chevron {
    height: rem-calc(17);
  }
}

.gift-giver-product-card {
  .atb-container {
    @include breakpoint(large only) {
      padding-right: rem-calc(8);
    }
  }

  @media print {
    page-break-inside: avoid;
    margin-top: rem-calc(24);
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
@include breakpoint(small down) {
  .product-card {
    .main-card {
      :deep(.product-description) {
        height: rem-calc(191);
      }

      .atb-container {
        margin-top: rem-calc(6);
      }
    }

    .in-store-card {
      :deep(.in-store-only) {
        margin-top: 0;
      }
    }
  }

  .registrant-product-card {
    .main-card {
      .edit-container {
        margin-bottom: rem-calc(6);
      }

      .footer-description {
        margin-top: rem-calc(12);
        height: rem-calc(40);
      }
    }

    .in-store-card {
      :deep(.product-description) {
        height: rem-calc(131);

        div {
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  .gift-giver-product-card {
    .main-card {
      .select-container {
        margin-top: 0;
        margin-bottom: rem-calc(6);
      }

      .cta-container {
        height: rem-calc(121);
        align-content: end;
      }

      .footer-description {
        height: rem-calc(40);
        margin-bottom: rem-calc(8);
        align-items: end;
      }

      .product-card-footer {
        height: rem-calc(169);
        margin-top: rem-calc(12);
      }

      .disabled-button-footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;

        .cta-container {
          height: rem-calc(50);
        }
      }

      .no-quantity-footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;

        .cta-container {
          height: rem-calc(50);
        }
      }
    }

    .in-store-card {
      :deep(.product-description) {
        height: rem-calc(130);
      }
    }
  }
}

@include breakpoint(medium only) {
  .product-card {
    .main-card {
      :deep(.product-description) {
        height: rem-calc(170);
      }

      .atb-container {
        margin-top: rem-calc(6);
      }

      .footer-description {
        height: rem-calc(40);
        align-items: end;
      }
    }

    .in-store-card {
      :deep(.product-description) {
        height: rem-calc(130);
      }

      :deep(.in-store-only) {
        margin-top: 0;
      }
    }
  }

  .registrant-product-card {
    .main-card {
      .edit-container {
        margin-bottom: rem-calc(6);
      }
    }
  }

  .gift-giver-product-card {
    .main-card {
      .select-container {
        margin-bottom: rem-calc(6);
        margin-top: 0;
      }

      .footer-description {
        margin-bottom: rem-calc(12);
      }

      .product-card-footer {
        height: rem-calc(164);
        margin-top: rem-calc(12);
      }

      .disabled-button-footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
      }

      .no-quantity-footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
      }
    }
  }
}

@include breakpoint(large up) {
  .product-card {
    .main-card {
      :deep(.product-description) {
        height: rem-calc(149);
        margin-top: 0;
      }

      .atb-container {
        margin-top: 0;
      }
    }

    .in-store-card {
      :deep(.product-description) {
        height: rem-calc(109);
      }

      :deep(.in-store-only) {
        margin-top: 0;
      }
    }
  }

  .registrant-product-card {
    .main-card {
      .edit-container {
        margin-top: 0;
      }
    }
  }

  .gift-giver-product-card {
    .main-card {
      .select-container {
        margin-top: 0;
        margin-bottom: 0;
      }

      .cta-container {
        height: rem-calc(59);
        display: flex;
        align-items: end;
      }

      .footer-description {
        height: rem-calc(20);
        margin-bottom: rem-calc(12);
      }

      .product-card-footer {
        height: rem-calc(91);
        margin-top: rem-calc(12);
      }

      .disabled-button-footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
      }

      .no-quantity-footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
      }
    }
  }
}

@media print {
  .gift-giver-product-card {
    .main-card {
      .product-thumbnail-wrapper-container {
        min-height: rem-calc(480);
        height: 100%;
      }

      :deep(.product-description) {
        min-height: rem-calc(160);
        height: 100%;
      }

      .cta-container {
        min-height: rem-calc(59);
        height: 100%;
      }

      .footer-description {
        min-height: rem-calc(20);
        height: 100%;
      }

      .product-card-footer {
        min-height: rem-calc(91);
        height: 100%;
      }

      .atb-container {
        padding-right: 0 !important;
      }

      .disabled-cta-container {
        padding-right: 0 !important;
      }
    }

    .in-store-card {
      :deep(.product-description) {
        min-height: rem-calc(100);
        height: 100%;
      }
    }
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
@include breakpoint(small down) {
  .product-card {
    .main-card {
      :deep(.product-description) {
        height: rem-calc(171);
      }

      .atb-container {
        margin-top: rem-calc(6);
      }
    }

    .in-store-card {
      :deep(.product-description) {
        height: rem-calc(111);
      }

      :deep(.in-store-only) {
        margin-top: 0;
      }
    }
  }

  .registrant-product-card {
    .main-card {
      .edit-container {
        margin-bottom: rem-calc(6);
      }

      .footer-description {
        height: rem-calc(36);
        margin-top: rem-calc(12);
      }
    }
  }

  .gift-giver-product-card {
    .main-card {
      .select-container {
        margin-bottom: rem-calc(6);
        margin-top: 0;
      }

      .footer-description {
        height: rem-calc(36);
        margin-bottom: rem-calc(8);
        align-items: end;
      }

      .product-card-footer {
        height: rem-calc(156);
        margin-top: rem-calc(12);
      }

      .disabled-button-footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;

        .cta-container {
          height: rem-calc(50);
        }
      }

      .no-quantity-footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;

        .cta-container {
          height: rem-calc(50);
        }
      }
    }
  }
}

@include breakpoint(medium only) {
  .product-card {
    .main-card {
      .atb-container {
        margin-top: rem-calc(6);
      }
    }

    .in-store-card {
      :deep(.in-store-only) {
        margin-top: 0;
      }
    }
  }

  .registrant-product-card {
    .main-card {
      :deep(.product-description) {
        height: rem-calc(150);
      }

      .edit-container {
        margin-bottom: rem-calc(6);
      }

      .footer-description {
        height: rem-calc(36);
        align-items: end;
      }
    }

    .in-store-card {
      :deep(.product-description) {
        height: rem-calc(110);
      }
    }
  }

  .gift-giver-product-card {
    .main-card {
      :deep(.product-description) {
        height: rem-calc(136);
      }

      .select-container {
        margin-bottom: rem-calc(6);
        margin-top: 0;
      }

      .footer-description {
        height: rem-calc(40);
        margin-bottom: rem-calc(12);
        align-items: end;
      }

      .product-card-footer {
        height: rem-calc(164);
        margin-top: rem-calc(12);
      }

      .disabled-button-footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
      }

      .no-quantity-footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
      }
    }

    .in-store-card {
      :deep(.product-description) {
        height: rem-calc(96);
      }
    }
  }
}

@include breakpoint(large up) {
  .product-card {
    .main-card {
      .atb-container {
        margin-top: 0;
      }
    }

    .in-store-card {
      :deep(.in-store-only) {
        margin-top: 0;
      }
    }
  }

  .registrant-product-card {
    .main-card {
      :deep(.product-description) {
        height: rem-calc(133);
        margin-top: 0;
      }

      .edit-container {
        margin-top: 0;
      }
    }

    .in-store-card {
      :deep(.product-description) {
        height: rem-calc(93);
      }
    }
  }

  .gift-giver-product-card {
    .main-card {
      :deep(.product-description) {
        height: rem-calc(135);
        margin-top: 0;
      }

      .select-container {
        margin-top: 0;
        margin-bottom: 0;
      }

      .cta-container {
        height: rem-calc(57);
        display: flex;
        align-items: end;
      }

      .footer-description {
        height: rem-calc(20);
        margin-bottom: rem-calc(12);
      }

      .product-card-footer {
        height: rem-calc(89);
        margin-top: rem-calc(12);
      }

      .disabled-button-footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
      }

      .no-quantity-footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
      }
    }

    .in-store-card {
      :deep(.product-description) {
        height: rem-calc(95);
      }
    }
  }
}

.registrant-product-card {
  .in-store-card {
    :deep(.in-store-only) {
      p {
        width: 100%;
        height: inherit;
        white-space: pre-line;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}

@media print {
  .gift-giver-product-card {
    .main-card {
      .product-thumbnail-wrapper-container {
        min-height: rem-calc(480);
        height: 100%;
      }

      :deep(.product-description) {
        min-height: rem-calc(145);
        height: 100%;
      }

      .cta-container {
        min-height: rem-calc(57);
        height: 100%;
      }

      .footer-description {
        min-height: rem-calc(40);
        height: 100%;
      }

      .product-card-footer {
        min-height: rem-calc(109);
        height: 100%;
      }

      .atb-container {
        padding-right: 0 !important;
      }

      .disabled-cta-container {
        padding-right: 0 !important;
      }
    }

    .in-store-card {
      :deep(.product-description) {
        min-height: rem-calc(85);
        height: 100%;
      }
    }
  }
}
</style>
